<template>

  <v-container id="certificatesSelectorMainContainer" fluid class="pa-0">

    <div>
      {{$t('appSignCertificatesSelector.labels.title')}} ({{certificatesCount}})
    </div>

    <div class="main-container pa-2" :class="isCertificatesVisible ? 'full-view' : 'short-view'">
      <div class="d-flex align-center justify-space-between clickable pr-1" @click="toggleCertificatesVisibility">

        <div class="d-flex justify-space-between">

          <v-btn color="primary" small :disabled="componentDisabled" @click.stop="selectCertificates">
            {{$t('buttons.add')}}
          </v-btn>

          <v-btn v-if="isClearBtnVisible" color="warning" small class="ml-1" :disabled="componentDisabled" @click.stop="clearCertificates">
            {{$t('buttons.clear')}}
          </v-btn>

        </div>

        <div>
          <v-btn small icon>
            <v-icon v-if="isCertificatesVisible" small>fa-angle-double-up</v-icon>
            <v-icon v-else small>fa-angle-double-down</v-icon>
          </v-btn>
        </div>

      </div>

      <div v-show="isCertificatesVisible">

        <dx-scroll-view :height="scrollHeight" class="my-2">

          <div class="certificates-grid">

            <div v-for="cert in certificates" :key="cert.uid" class="d-flex align-center justify-space-between certificate-row">

              <div class="text-truncate">{{cert.fileName}}</div>

              <div v-if="!componentDisabled">
                <v-btn icon small @click="deleteCertificate(cert)">
                  <v-icon small>fa-trash</v-icon>
                </v-btn>
              </div>

            </div>

          </div>

        </dx-scroll-view>

      </div>
    </div>

    <input ref="fileCertificates" type="file" hidden multiple :accept="inputAcceptAttr" @change="onFileChange">

  </v-container>

</template>

<script>
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { Guid } from 'guid-typescript';

import { logger } from '@/helpers/log.service';
import { messageService } from '@/helpers/message.service';
import { arrayBufferToBase64 } from '@/helpers/utility';
import { actions as _act } from '@/models/store.actions';
import { isNativeApp } from '@/helpers/platform-helper';
import { getLocale } from '@/helpers';

export default {
  name: 'AppSignCertificatesSelector',
  components: { DxScrollView },
  props: {
    componentDisabled: { default: false }
  },
  data() {
    return {
      allowedCertificateExtensions: ['cer', 'crt'],
      isCertificatesVisible: false
    };
  },
  computed: {
    certificates() {
      return this.storedCertificates.map((c) => ({
        uid: c.uid,
        fileName: c.fileName
      }));
    },
    certificatesCount() {
      return this.storedCertificates.length;
    },
    currentUserEmail() {
      return this.$store.getters[_act.AUTH_GET_EMAILUSERGET] || '';
    },
    inputAcceptAttr() {
      return isNativeApp() ? null : this.allowedCertificateExtensions.map((e) => `.${e}`).join(',');
    },
    isClearBtnVisible() {
      return this.certificatesCount > 0;
    },
    scrollHeight() {
      return this.storedCertificates.length > 5 ? '200px' : null;
    },
    storedCertificates() {
      return this.$store.getters[_act.SIGN_GET_USER_CERT](this.currentUserEmail);
    }
  },
  methods: {
    clearCertificates() {
      this.$store.commit(_act.SIGN_MUT_CLEAR_USER_CERTS, {
        email: this.currentUserEmail
      });
    },
    deleteCertificate(cert) {
      this.$store.commit(_act.SIGN_MUT_DEL_USER_CERT, {
        email: this.currentUserEmail,
        certUid: cert.uid
      });
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (files.length > 0) {
        const readPromises = [];

        Array.from(files)
          .filter((f) => !!f.name)
          .forEach((f) => readPromises.push(this.readFile(f)));

        Promise.all(readPromises)
          .then((res) => {
            const errors = res ? res.filter((r) => !!r && !!r.error).map((r) => r.error) : [];
            if (errors.length) {
              const errorMsg = errors.reduce((pv, cv) => `${pv}\n${cv}`, '');
              if (errorMsg) {
                messageService.error({ message: errorMsg });
              }
            }
          })
          .catch((err) => {
            messageService.error({ message: err.message });
          });
      }
    },
    readFile(file) {
      let arr = file.name.split('.');
      let extension = arr.length > 0 ? arr[arr.length - 1] : '';
      if (!extension || !this.allowedCertificateExtensions.includes(extension)) {
        return Promise.reject(new Error(getLocale('appSignCertificatesSelector.notifications.extensionError', { extension })));
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onerror = (e) => {
          const err = e.error && e.error.message ? e.error.message : e.message;
          logger.error(`[CertificatesSelector:readFile] Error occurred reading file: ${file.name}`, e);
          resolve({ error: `${file.name}: ${err}` });
        };

        reader.onload = (e) => {
          this.saveCertificate({
            uid: Guid.raw(),
            fileName: file.name,
            content: arrayBufferToBase64(new Uint8Array(e.target.result))
          });
          resolve();
        };
        reader.readAsArrayBuffer(file);
      });
    },
    saveCertificate(cert) {
      this.$store.commit(_act.SIGN_MUT_ADD_USER_CERT, {
        email: this.currentUserEmail,
        cert
      });
    },
    selectCertificates() {
      this.$refs.fileCertificates.value = '';
      this.$refs.fileCertificates.click();
    },
    toggleCertificatesVisibility() {
      this.isCertificatesVisible = !this.isCertificatesVisible;
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/variables.scss';

#certificatesSelectorMainContainer {
  .main-container {
    border: 1px solid var(--v-secondary-base);
    border-radius: $app-defaults-text-field-border-radius;
    background-color: var(--v-app-colors-background-light-base);
    &.short-view {
      max-height: 42px;
    }
  }
  .certificate-row {
    padding: 4px;
    border-top: 1px solid var(--v-secondary-base);
  }
  .certificate-row:last-child {
    border-bottom: 1px solid var(--v-secondary-base);
  }
}
</style>
